import {
  DriftController,
  gradeSync,
  projectPosition,
  type DriftStats,
  type PlaybackState,
  type QualityGrade,
} from '@cinesync/sync-core';
import type { MediaEngine } from '@/modules/media/engine';

/**
 * The sync loop.
 *
 * This is where the parts finally meet: the room's playback line says where
 * the film should be, the clock estimate converts that into local time, the
 * engine reports where it actually is, and the drift controller decides what
 * to do about the difference.
 *
 * Everything it needs arrives through its constructor, including the clock,
 * so the whole loop can be driven in a test against a fake engine and a
 * synthetic clock rather than requiring a network and a real decoder.
 */

/** How often the loop compares the engine against the room. */
export const TICK_INTERVAL_MS = 250;

/**
 * Ignore the engine for this long after commanding a seek.
 *
 * A seeking player reports its old position for a few frames. Without this
 * grace period the controller sees a huge error, seeks again, and the two
 * chase each other indefinitely.
 */
const SEEK_SETTLE_MS = 700;

export interface SyncControllerOptions {
  readonly engine: MediaEngine;
  /** Converts a local timestamp into the relay's frame of reference. */
  readonly toServerTime: (localMs: number) => number;
  /** Monotonic local clock. Injected so tests can drive it. */
  readonly now: () => number;
  /** Called whenever the readout should update. */
  readonly onStats?: (stats: SyncStats) => void;
}

export interface SyncStats {
  readonly errorMs: number;
  readonly grade: QualityGrade;
  readonly drift: DriftStats;
  /** True while the engine is being left alone deliberately. */
  readonly suspended: boolean;
}

export class SyncController {
  readonly #engine: MediaEngine;
  readonly #toServerTime: (localMs: number) => number;
  readonly #now: () => number;
  readonly #onStats: ((stats: SyncStats) => void) | undefined;
  readonly #drift = new DriftController();

  #room: PlaybackState | null = null;
  /** The speed the user chose, before drift correction touches it. */
  #baseRate = 1;
  #seekingUntil = 0;
  #enabled = false;

  constructor(options: SyncControllerOptions) {
    this.#engine = options.engine;
    this.#toServerTime = options.toServerTime;
    this.#now = options.now;
    this.#onStats = options.onStats;
  }

  /**
   * Adopt a new room state.
   *
   * Applies the coarse change immediately, pausing, resuming or jumping as
   * required, and leaves the fine correction to the tick loop.
   */
  apply(state: PlaybackState): void {
    this.#room = state;
    this.#baseRate = state.rate;
    this.#enabled = true;

    const serverNow = this.#toServerTime(this.#now());
    const target = projectPosition(state, serverNow);

    if (state.paused) {
      this.#engine.pause();
      // A pause is an exact instruction, so the position is set outright
      // rather than walked to.
      this.#hardSeek(target);
      this.#drift.reset();
      return;
    }

    const awaitingStart = state.startAtServerMs !== null && serverNow < state.startAtServerMs;
    if (awaitingStart) {
      // Hold at the anchor and let the tick loop release at the agreed moment,
      // which is what makes both machines begin together.
      this.#engine.pause();
      this.#hardSeek(target);
      return;
    }

    const actual = this.#engine.snapshot.positionMs;
    if (Math.abs(target - actual) > 1_000) this.#hardSeek(target);

    this.#engine.setRate(this.#baseRate);
    void this.#engine.play();
  }

  /**
   * One iteration of the loop. Call every `TICK_INTERVAL_MS`.
   *
   * Separated from any timer so a test can drive it directly and run an hour
   * of playback in milliseconds.
   */
  tick(): void {
    const state = this.#room;
    if (state === null || !this.#enabled) return;

    const localNow = this.#now();
    const serverNow = this.#toServerTime(localNow);
    const snapshot = this.#engine.snapshot;

    if (state.paused) {
      if (!snapshot.paused) this.#engine.pause();
      return;
    }

    // Release a scheduled start exactly when it comes due.
    if (state.startAtServerMs !== null && serverNow < state.startAtServerMs) {
      if (!snapshot.paused) this.#engine.pause();
      return;
    }
    if (snapshot.paused) void this.#engine.play();

    const target = projectPosition(state, serverNow);

    // A stalled or freshly seeked engine reports a position that means
    // nothing, and correcting against it would inject a real error.
    const suspended = snapshot.stalled || localNow < this.#seekingUntil;

    const correction = this.#drift.evaluate({
      targetPositionMs: target,
      actualPositionMs: snapshot.positionMs,
      baseRate: this.#baseRate,
      suspended,
    });

    switch (correction.kind) {
      case 'hold':
        this.#engine.setRate(correction.playbackRate);
        break;
      case 'rate':
        this.#engine.setRate(correction.playbackRate);
        break;
      case 'seek':
        this.#hardSeek(correction.toPositionMs);
        this.#engine.setRate(this.#baseRate);
        break;
    }

    this.#publish(suspended);
  }

  /** Stop correcting, for leaving a room or closing the player. */
  disable(): void {
    this.#enabled = false;
    this.#room = null;
    this.#engine.setRate(this.#baseRate);
    this.#drift.reset();
  }

  get stats(): SyncStats {
    const drift = this.#drift.stats;
    return {
      errorMs: drift.smoothedErrorMs,
      grade: gradeSync(drift.smoothedErrorMs),
      drift,
      suspended: this.#now() < this.#seekingUntil,
    };
  }

  #hardSeek(positionMs: number): void {
    this.#engine.seek(positionMs);
    // Give the engine time to settle before believing its position again.
    this.#seekingUntil = this.#now() + SEEK_SETTLE_MS;
  }

  #publish(suspended: boolean): void {
    if (this.#onStats === undefined) return;
    const drift = this.#drift.stats;
    this.#onStats({
      errorMs: drift.smoothedErrorMs,
      grade: gradeSync(drift.smoothedErrorMs),
      drift,
      suspended,
    });
  }
}
